/**
 * Atasamente — listare batched, incarcare cu progres, stergere.
 *
 * Ca si la comentarii, listarea e batched pentru ca pictograma de clips e vizibila pe fiecare rand:
 * `entity_ids=1,2,3` intr-o singura cerere in loc de 30.
 *
 * Incarcarea trece prin `XMLHttpRequest` (vezi `lib/api.ts`), singurul API de browser care
 * raporteaza progresul de UPLOAD. Un extras de 12 MB fara bara de progres arata exact ca o
 * aplicatie blocata, iar peste un VPN de acasa asta e situatia normala, nu exceptia.
 *
 * `inlineUrl` e un link SEMNAT cu TTL scurt: se poate pune direct in `<img>` sau `<iframe>`, pentru
 * ca subresursele nu duc previzibil cookie-ul de sesiune. Nu se cache-uieste si nu se
 * pastreaza: expira in cateva minute, deci lista se cere din nou cand se redeschide panoul.
 */

import { useMemo } from 'react';
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api, incarcaFisier } from '../lib/api.ts';
import { chei, normalizeazaIds } from '../lib/query-keys.ts';
import type { AttachmentUploadFields } from '../../../../functions/api/schemas.ts';

export type TipEntitateAtasament = AttachmentUploadFields['entityType'];
export type EtichetaAtasament = NonNullable<AttachmentUploadFields['label']>;

export interface Atasament {
  id: number;
  entityId: number | null;
  filename: string;
  ext: string;
  mimeSniffed: string;
  sizeBytes: number;
  sha256: string;
  label: string | null;
  /** `pending` cat timp nu exista scanare antivirus — se afiseaza explicit, nu se ascunde. */
  scanStatus: string;
  refCount: number;
  uploadedAt: number;
  uploadedByName: string | null;
  downloadUrl: string;
  /** Link semnat HMAC, TTL scurt. Bun pentru `<img>`; NU se stocheaza nicaieri. */
  inlineUrl: string;
}

export interface RaspunsAtasamente {
  entityType: string;
  attachments: Atasament[];
}

export function useAtasamente(
  entityType: TipEntitateAtasament,
  entityIds: readonly number[],
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsAtasamente, Error> {
  const ids = useMemo(() => normalizeazaIds(entityIds), [entityIds]);

  return useQuery({
    queryKey: chei.atasamente.lot(entityType, ids),
    queryFn: ({ signal }) =>
      api.get<RaspunsAtasamente>('/attachments', {
        interogare: { entity_type: entityType, entity_ids: ids },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false && ids.length > 0,
    // `inlineUrl` e semnat cu TTL de ~5 minute: tinut mai mult, ar da 401 la afisare.
    staleTime: 60_000,
    gcTime: 4 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// POST /api/attachments — multipart
// ---------------------------------------------------------------------------

export interface RaspunsIncarcare {
  id: number;
  entityType: string;
  entityId: number | null;
  filename: string;
  ext: string;
  mimeSniffed: string;
  mimeDeclared: string | null;
  sizeBytes: number;
  sha256: string;
  label: string | null;
  scanStatus: string;
  /** `true` cand acelasi conținut exista deja in R2: se stocheaza O SINGURA data. */
  deduplicated: boolean;
  /** De exemplu „extensia declarata nu corespunde conținutului; salvat ca `.pdf`". */
  warnings: string[];
  downloadUrl: string;
  inlineUrl: string;
  uploadedAt: number;
}

export interface VariabileIncarcare {
  entityType: TipEntitateAtasament;
  entityId: number | null;
  fisier: File;
  label?: EtichetaAtasament;
  /** 0..1. Vine din evenimentele de upload ale XHR-ului. */
  onProgres?: (fractie: number) => void;
  semnal?: AbortSignal;
}

export function fnIncarcaAtasament(variabile: VariabileIncarcare): Promise<RaspunsIncarcare> {
  const formular = new FormData();
  formular.set('file', variabile.fisier);
  formular.set('entity_type', variabile.entityType);
  // Doar `doc_note` are voie sa existe fara entitate; campul gol e forma pe care o asteapta ruta.
  formular.set('entity_id', variabile.entityId === null ? '' : String(variabile.entityId));
  if (variabile.label) formular.set('label', variabile.label);

  return incarcaFisier<RaspunsIncarcare>('/attachments', formular, {
    onProgres: variabile.onProgres,
    semnal: variabile.semnal,
  });
}

/**
 * Incarcarea NU se pune in coada offline.
 *
 * Un `File` nu se serializeaza in IndexedDB odata cu variabilele mutatiei (referinta lui moare la
 * reload), deci o incarcare „reluata" ar retrimite un formular gol si ar parea ca a reusit.
 * `networkMode: 'always'` o lasa sa esueze imediat cand nu e retea, cu un mesaj clar, in loc sa fie
 * pusa in asteptare pe termen nelimitat.
 */
export function useIncarcaAtasament(): UseMutationResult<
  RaspunsIncarcare,
  Error,
  VariabileIncarcare
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsIncarcare, Error, VariabileIncarcare>({
    mutationKey: chei.mutatii.incarcaAtasament(),
    mutationFn: fnIncarcaAtasament,
    networkMode: 'always',
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.atasamente.tot() });
      // Contorul de atasamente se afiseaza pe randul din grila si in matrice.
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
    },
  });
}

/** Progresul incarcarilor in curs, pentru bara din drawer. */
export function useProgresIncarcari(): readonly { status: string; fisier: string }[] {
  return useMutationState({
    filters: { mutationKey: chei.mutatii.incarcaAtasament(), status: 'pending' },
    select: (mutatie) => ({
      status: mutatie.state.status,
      fisier: (mutatie.state.variables as VariabileIncarcare | undefined)?.fisier.name ?? '',
    }),
  });
}

// ---------------------------------------------------------------------------
// DELETE /api/attachments/:id
// ---------------------------------------------------------------------------

export interface RaspunsStergere {
  id: number;
  deleted: boolean;
  alreadyDeleted?: boolean;
  /** Soft delete: obiectul din R2 se sterge dupa 30 de zile, deci greseala e reversibila. */
  purgeAfter?: number;
}

export function fnStergeAtasament(variabile: { id: number }): Promise<RaspunsStergere> {
  return api.sterge<RaspunsStergere>(`/attachments/${variabile.id}`);
}

export function useStergeAtasament(): UseMutationResult<RaspunsStergere, Error, { id: number }> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsStergere, Error, { id: number }>({
    mutationKey: chei.mutatii.stergeAtasament(),
    mutationFn: fnStergeAtasament,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.atasamente.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/attachments/:id/scan
// ---------------------------------------------------------------------------

/**
 * Marcheaza rezultatul unei scanari antivirus.
 *
 * Ruta o va apela jobul, cu service token; hookul exista pentru pagina de administrare, ca statusul
 * `pending` afisat pe fiecare fisier sa poata fi inchis manual atunci cand scanarea se face separat.
 */
export function useMarcheazaScanare(): UseMutationResult<
  { id: number; scanStatus: string },
  Error,
  { id: number; curat: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; scanStatus: string }, Error, { id: number; curat: boolean }>({
    mutationKey: chei.mutatii.marcheazaScanare(),
    mutationFn: (variabile) =>
      api.post<{ id: number; scanStatus: string }>(`/attachments/${variabile.id}/scan`, {
        enabled: variabile.curat,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chei.atasamente.tot() });
    },
  });
}
